/* EventoConfermaPage.jsx — Conferma iscrizione evento
 * UX: tracking ID copiabile, CTA "Crea Area Cliente" XL full-width mobile.
 */
import { useState } from "react";
import { useLocation, Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  CheckCircle2, ArrowRight, Sparkles, Home, Copy, Check,
} from "lucide-react";

export default function EventoConfermaPage() {
  const { state } = useLocation();
  const { slug } = useParams();
  const nav = useNavigate();
  const r = state || {};
  const inviteToken = r.debug_invite_token;
  const inviteSent = r.auto_invite_status === "invite_sent";
  const [copied, setCopied] = useState(false);

  const copyTracking = async () => {
    if (!r.tracking_id) return;
    try {
      await navigator.clipboard.writeText(r.tracking_id);
      setCopied(true);
      toast.success("Tracking ID copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 via-white to-emerald-50">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-10 sm:py-12">
        <div
          data-testid="evento-conferma-card"
          className="max-w-2xl w-full bg-white rounded-3xl border border-emerald-200 shadow-2xl p-6 sm:p-8 md:p-12 text-center"
        >
          {/* Success icon — animato */}
          <div className="mx-auto w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mb-5 ring-4 ring-emerald-200 animate-[scale-in_0.4s_ease-out]">
            <CheckCircle2 className="w-14 h-14 text-emerald-600" aria-hidden="true" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-[#163321] mb-3">
            🎉 Iscrizione confermata!
          </h1>
          <p className="text-base text-slate-600 mb-5 leading-relaxed">
            Ti contatteremo entro <b>48 ore lavorative</b> per la consulenza
            gratuita su quanto ci hai segnalato.
          </p>

          {/* Tracking ID card grande + copy */}
          {r.tracking_id && (
            <div
              data-testid="evento-tracking-card"
              className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-4 mb-5"
            >
              <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
                Tracking ID
              </p>
              <div className="flex items-center justify-center gap-2">
                <code
                  data-testid="evento-tracking-id"
                  className="px-3 py-2 rounded-lg bg-white border border-slate-300 font-mono text-base sm:text-lg font-bold text-[#163321] break-all"
                >
                  {r.tracking_id}
                </code>
                <button
                  type="button"
                  onClick={copyTracking}
                  aria-label="Copia tracking ID"
                  data-testid="evento-copy-tracking"
                  className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg bg-[#163321] text-white hover:bg-[#0e1f15] active:scale-95 transition-transform focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#163321]"
                >
                  {copied
                    ? <Check className="w-5 h-5" aria-hidden="true" />
                    : <Copy className="w-5 h-5" aria-hidden="true" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                Conserva questo codice per riferimenti futuri.
              </p>
            </div>
          )}

          {r.incentivo && (
            <p className="text-sm text-amber-800 bg-amber-100 border border-amber-200 px-4 py-2.5 rounded-xl inline-flex items-center gap-2 mb-2 font-bold">
              <Sparkles className="w-4 h-4" aria-hidden="true" /> {r.incentivo}
            </p>
          )}

          {/* CTA Area Cliente XL */}
          {inviteSent && (
            <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#163321] to-[#0e1f15] text-white p-6 sm:p-7 text-left shadow-xl">
              <h2 className="text-xl sm:text-2xl font-black mb-2">
                Vuoi velocizzare la consulenza?
              </h2>
              <p className="text-emerald-100 text-sm sm:text-base mb-5 leading-relaxed">
                Crea il tuo <b>Area Cliente</b> in 30 secondi: i tuoi dati
                sono già pronti, ti basta una password. Vedrai stato richiesta,
                polizze, documenti.
              </p>
              <Link
                to={inviteToken
                  ? `/area-cliente/register?token=${inviteToken}`
                  : "/area-cliente/register"}
                data-testid="evento-cta-create-account"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[60px] px-8 py-4 rounded-full bg-amber-400 text-[#163321] font-black text-base sm:text-lg shadow-lg hover:bg-amber-300 active:scale-[0.98] transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-200"
              >
                Crea Area Cliente in 30 secondi
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </div>
          )}

          {/* Bottoni navigazione secondari */}
          <div className="flex flex-col sm:flex-row gap-2 mt-7 justify-center">
            <button
              onClick={() => nav(`/eventi/${slug}`)}
              className="min-h-[48px] px-5 py-3 rounded-full bg-slate-100 text-sm font-bold text-slate-700 hover:bg-slate-200 active:scale-95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-400"
              data-testid="evento-back-event"
            >
              Torna all'evento
            </button>
            <button
              onClick={() => nav("/")}
              className="min-h-[48px] px-5 py-3 rounded-full bg-slate-100 text-sm font-bold text-slate-700 hover:bg-slate-200 active:scale-95 transition-all inline-flex items-center gap-2 justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-400"
              data-testid="evento-back-home"
            >
              <Home className="w-4 h-4" aria-hidden="true" /> Torna alla home
            </button>
          </div>
        </div>
      </main>
      <Footer />
      <style>{`
        @keyframes scale-in {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
